/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender
} from '@tanstack/react-table';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { debounce } from 'lodash';
import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';
import { TableFooter } from './TableFooter';
import { useApi } from 'hooks/useApis';
import { getDynamicReportdata } from 'api/DynamicReportApi';
import { useTableData } from './useTableData';
import { ColumnVisibilityModal } from './ColumnVisibilityModal';
import { getCommonPinningStyles, HeaderCell } from './HeaderCell';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import FalconCardHeader from 'components/common/FalconCardHeader';
import CardDropdown from 'components/common/CardDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import Pagin from 'components/doc-components/Pagination';
import { getTenantFromToken } from 'utils/routeUtils';
import { SERVICES } from 'api/common/constants';

const DEFAULT_PAGE_SIZE = 25;
const DEBOUNCE_DELAY = 500;
const DEFAULT_COLUMN_WIDTH = 400; // Set default column width to 400px

const Table = ({
  apiPath,
  forWeather = false,
  district_ids,
  initialPageSize = DEFAULT_PAGE_SIZE,
  handleAction,
  dateRangePicker,
  buttons,
  title,
  handleDownload,
  showHeader,
  showFooter,
  errorMsg,
  dateFilter,
  exportKey,
  dropdownFilter,
  dropdownData,
  dropdownOptions,
  selectedValues,
  setSelectedValues,
  service = SERVICES.DIGITIZATION
}) => {
  // Use the custom API hook
  const {
    request: fetchData,
    data: apiData,
    loading,
    error
  } = useApi(getDynamicReportdata);

  const tenant = getTenantFromToken();
  const navigate = useNavigate();

  // State management
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [remainingRecords, setRemainingRecords] = useState(0);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [columnPinning, setColumnPinning] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [cursor, setCursor] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const [columnFilters, setColumnFilters] = useState([]);
  const [queryParams, setQueryParams] = useState({
    pageIndex: 0,
    pageSize: initialPageSize,
    sorting: [],
    globalSearch: '',
    dateRange: {
      start_date: '',
      end_date: ''
    }
  });

  // Table configuration
  const [tableConfig, setTableConfig] = useState({
    columnPinning: {},
    columnOrder: [],
    columnVisibility: {},
    columnResizing: {}
  });

  // Ref for direct refresh function
  const refreshTableRef = useRef(null);

  // Fetch data handler
  const handleFetchData = useCallback(async () => {
    const cursor = queryParams.pageIndex * queryParams.pageSize;
    const filter = {
      search_text: queryParams.globalSearch,
      start_date: queryParams.dateRange.start_date,
      end_date: queryParams.dateRange.end_date
    };

    try {
      const params = {
        apiPath,
        cursor,
        limit: queryParams.pageSize,
        tenant,
        filter,
        service,
        dropdownOptions,
        selectedValues
      };

      if (forWeather && district_ids) {
        params.district_ids = district_ids;
      }
      await fetchData(params);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [
    queryParams,
    apiPath,
    tenant,
    district_ids,
    forWeather,
    fetchData,
    selectedValues
  ]);

  // Create a direct refresh function that calls the API
  const refreshTable = useCallback(() => {
    console.log('Directly refreshing Table data with API call');
    // Reset to first page and make API call
    setQueryParams(prev => ({ ...prev, pageIndex: 0 }));

    // Force service to be DIGITIZATION for refresh operations
    const refreshParams = {
      apiPath,
      cursor: 0,
      limit: queryParams.pageSize,
      tenant,
      filter: {
        search_text: queryParams.globalSearch,
        start_date: queryParams.dateRange.start_date,
        end_date: queryParams.dateRange.end_date
      },
      service: SERVICES.DIGITIZATION, // Always use DIGITIZATION service
      dropdownOptions,
      selectedValues
    };

    if (forWeather && district_ids) {
      refreshParams.district_ids = district_ids;
    }

    fetchData(refreshParams);
  }, [
    apiPath,
    queryParams,
    tenant,
    district_ids,
    forWeather,
    fetchData,
    dropdownOptions,
    selectedValues
  ]);

  // Expose the refresh function via ref and global window property
  useEffect(() => {
    refreshTableRef.current = refreshTable;

    if (typeof window !== 'undefined') {
      window.refreshTableData = refreshTable;
    }

    return () => {
      if (typeof window !== 'undefined') {
        delete window.refreshTableData;
      }
    };
  }, [refreshTable]);

  // Update table data when API response changes
  useEffect(() => {
    if (apiData?.results) {
      setTableData(apiData.results.data || []);
      setTotalRows(apiData.results.total || 0);
      setRemainingRecords(apiData.results.remaining_records || 0);
      setCursor(apiData.results.next_cursor);

      // Set column visibility from API response
      if (apiData.results.column_visibility) {
        setColumnVisibility(prevVisibility => ({
          ...prevVisibility,
          ...apiData.results.column_visibility
        }));
      }
    }
  }, [apiData]);

  // Initial data fetch and refetch on params change
  useEffect(() => {
    handleFetchData();
  }, [handleFetchData]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce(value => {
      setQueryParams(prev => ({
        ...prev,
        pageIndex: 0,
        globalSearch: value // set globalFilter to the value directly
      }));
    }, DEBOUNCE_DELAY),
    []
  );

  const { columns, fuzzyFilter } = useTableData(tableData);

  // Table instance
  const table = useReactTable({
    data: tableData,
    columns,
    defaultColumn: {
      minSize: 60,
      // maxSize: 800,
      size: DEFAULT_COLUMN_WIDTH
    },
    state: {
      columnFilters,
      showFilters,
      pagination: {
        pageIndex: queryParams.pageIndex,
        pageSize: queryParams.pageSize
      },
      columnPinning,
      columnVisibility,
      columnResizing: tableConfig.columnResizing,
      rowSelection: {}
    },
    enableColumnFilters: true,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      fuzzy: fuzzyFilter
    },
    manualPagination: true,
    pageCount: Math.ceil(totalRows / queryParams.pageSize)
  });

  const clearFilters = useCallback(() => {
    setColumnFilters([]);
  }, []);
  const toggleFilters = useCallback(() => {
    setShowFilters(prev => {
      if (prev) {
        clearFilters();
      }
      return !prev;
    });
  }, [clearFilters]);

  // Event handlers
  const handlePageChange = newPage => {
    setQueryParams(prev => ({
      ...prev,
      pageIndex: newPage
    }));
  };

  const handlePageSizeChange = newSize => {
    setQueryParams(prev => ({
      ...prev,
      pageSize: newSize,
      pageIndex: 0
    }));
  };

  // if (error) {
  //   return <div className="alert alert-danger">Error: {error}</div>;
  // }
  const handleDateRangeChange = (date, field) => {
    if (date) {
      setQueryParams(prev => ({
        ...prev,
        pageIndex: 0,
        dateRange: {
          ...prev.dateRange,
          [field]: date // Dynamically update start_date or end_date
        }
      }));
    } else {
      // If no date, clear the field
      setQueryParams(prev => ({
        ...prev,
        pageIndex: 0,
        dateRange: {
          ...prev.dateRange,
          [field]: ''
        }
      }));
    }
  };

  const filteredRowsCount = table.getFilteredRowModel().rows.length;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="d-flex flex-column h-100 card">
        {showHeader && (
          <>
            <div className="">
              <FalconCardHeader
                title={title}
                titleTag="h5"
                light={true}
                endEl={
                  <div className="d-flex justify-content-end my-auto">
                    <div className="ml-2">
                      {buttons
                        ?.filter(button => button.enabled)
                        .map((button, index) => (
                          <Button
                            key={index}
                            variant="primary"
                            size="sm"
                            onClick={() =>
                              navigate(`/${tenant}${button.redirectPath}`)
                            }
                            className={index > 0 ? 'ms-2' : ''} // Apply spacing only to subsequent buttons
                          >
                            {button.buttonName}
                          </Button>
                        ))}
                    </div>
                    <CardDropdown
                      showColumnVisiblity={setShowColumnSelector}
                      onExport={handleDownload}
                      toggleFilters={toggleFilters}
                      exportKey={exportKey}
                    />
                  </div>
                }
              />
              <div className="d-flex justify-content-between align-items-center px-3">
                {/* <button
              type="button"
              className="btn btn-outline-primary"
              onClick={toggleFilters}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button> */}
              </div>
            </div>
            <div className="flex-shrink-0 d-flex mt-3">
              <TableHeader
                table={table}
                setShowColumnSelector={setShowColumnSelector}
                showColumnSelector={showColumnSelector}
                handleGlobalSearch={debouncedSearch}
                dateRangePicker={dateRangePicker}
                globalfilters={queryParams}
                handleDateChange={handleDateRangeChange}
                dateFilter={dateFilter}
                dropdownFilter={dropdownFilter}
                dropdownData={dropdownData}
                dropdownOptions={dropdownOptions}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
              />
            </div>
          </>
        )}

        <div className="flex-grow-1 overflow-auto position-relative">
          {loading && (
            <div
              className="position-absolute w-100 h-100 bg-white bg-opacity-75 d-flex justify-content-center align-items-center"
              style={{ zIndex: 1000 }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          <table className="table table-bordered table-hover table-striped w-100">
            <thead
              className="bg-200 text-900 text-nowrap align-middle position-sticky"
              style={{
                zIndex: 3,
                backgroundColor: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                top: -1
              }}
            >
              {table.getHeaderGroups().map(headerGroup => (
                <tr
                  key={headerGroup.id}
                  className="align-middle white-space-nowrap"
                >
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="fw-semibold text-start border"
                      style={{
                        ...getCommonPinningStyles(
                          header.column,
                          table.getAllLeafColumns()
                        ) // Pass all columns
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <HeaderCell
                          header={header}
                          table={table} // Pass the table instance
                          showFilter={showFilters}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <TableBody
              table={table}
              loading={loading}
              onAction={handleAction}
              data={tableData}
              onPageSizeChange={handlePageSizeChange}
              globalfilters={queryParams}
              errorMsg={errorMsg}
            />
          </table>
        </div>
        {showFooter && (
          <TableFooter
            table={table}
            totalRows={filteredRowsCount}
            next_cursor={cursor}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            canNextPage={remainingRecords > 0}
            canPreviousPage={queryParams.pageIndex > 0}
            loading={loading}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        )}
      </div>
      <ColumnVisibilityModal
        isOpen={showColumnSelector}
        onClose={() => setShowColumnSelector(false)}
        table={table}
      />
    </DndProvider>
  );
};
export default Table;
